import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, LogIn, Server } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../providers/AuthProvider';
import { authService } from '../../services/api';
import ThemeToggle from '../common/ThemeToggle';

function Login() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  // Le login classique (email/mdp) est replié par défaut — réservé à l'admin.
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    if (error === 'sso_failed') {
      const reason = params.get('reason');
      const message = reason
        ? `Échec de la connexion Atlassian : ${reason}`
        : 'Échec de la connexion Atlassian';
      toast.error(message);
      window.history.replaceState(null, '', '/login');
    }
  }, [location.search]);

  const handleSsoLogin = async () => {
    setSsoLoading(true);
    try {
      const data = await authService.startSsoLogin();
      window.location.href = data.authUrl;
    } catch (error) {
      if (error.response?.status === 503) {
        toast.error("Le SSO n'est pas configuré sur ce serveur");
      } else {
        toast.error('Erreur lors du démarrage de la connexion SSO');
      }
      setSsoLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!identifier.trim() || !password.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);

    try {
      const result = await login(identifier.trim(), password);

      if (result.success) {
        toast.success('Connexion réussie !');
        navigate('/dashboard', { replace: true });
      } else {
        toast.error(result.error || 'Identifiants invalides');
      }
    } catch (_error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-slate-950 flex items-center justify-center px-4 py-12">
      {/* ThemeToggle top-right — persiste le choix pour après login */}
      <div className="absolute top-4 right-4 z-20">
        <ThemeToggle variant="dark" />
      </div>

      {/* Blobs décoratifs animés — effet "liquid glass" type Apple/Stripe */}
      <motion.div
        aria-hidden="true"
        className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 opacity-40 blur-[120px]"
        animate={{ x: [0, 60, 0], y: [0, -40, 0] }}
        transition={{ duration: 18, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute bottom-[-15%] right-[-10%] w-[600px] h-[600px] rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-500 opacity-40 blur-[120px]"
        animate={{ x: [0, -50, 0], y: [0, 60, 0] }}
        transition={{ duration: 22, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute top-[30%] right-[20%] w-[400px] h-[400px] rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 opacity-30 blur-[100px]"
        animate={{ x: [0, 40, 0], y: [0, 40, 0] }}
        transition={{ duration: 20, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
      />

      {/* Noise overlay subtil pour texture */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.03] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
        }}
      />

      {/* Contenu */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="relative w-full max-w-md space-y-8"
      >
        {/* Logo et titre */}
        <div className="text-center">
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.5, ease: 'backOut' }}
            className="relative mx-auto h-20 w-20 mb-6"
          >
            {/* Glow derrière le logo */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-400 to-cyan-400 blur-xl opacity-60" />
            <div className="relative h-full w-full rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-2xl shadow-blue-500/30 ring-1 ring-white/20">
              <Server className="h-9 w-9 text-white drop-shadow-lg" />
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-3xl font-bold tracking-tight bg-gradient-to-b from-white to-white/70 bg-clip-text text-transparent"
          >
            Previsoft Monitoring
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="mt-2 text-sm text-slate-400"
          >
            Connectez-vous avec votre compte Atlassian
          </motion.p>
        </div>

        {/* Carte glass */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="relative rounded-3xl p-8 backdrop-blur-2xl bg-white/5 ring-1 ring-white/10 shadow-[0_10px_60px_-10px_rgba(0,0,0,0.6)]"
        >
          {/* Highlight top — bord supérieur brillant type Apple */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-px rounded-t-3xl bg-gradient-to-r from-transparent via-white/30 to-transparent"
          />

          {/* CTA principal : Se connecter avec Atlassian */}
          <motion.button
            type="button"
            disabled={ssoLoading}
            whileHover={{ scale: ssoLoading ? 1 : 1.015 }}
            whileTap={{ scale: ssoLoading ? 1 : 0.985 }}
            onClick={handleSsoLogin}
            className="group relative w-full flex justify-center items-center gap-2.5 px-4 py-3.5 text-sm font-semibold text-white rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
          >
            {/* Shine effect au hover */}
            <span
              aria-hidden="true"
              className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:translate-x-full transition-transform duration-700"
            />
            {ssoLoading ? (
              <>
                <div className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                <span>Redirection…</span>
              </>
            ) : (
              <>
                {/* Logo Atlassian (blanc, monochrome pour le CTA) */}
                <svg
                  aria-hidden="true"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.356 14.007c-.32-.434-.666-.902-.97-1.322a.46.46 0 0 0-.758 0L3.06 17.604a.46.46 0 0 0 .379.718h5.232a.463.463 0 0 0 .38-.201c.667-.97.61-2.678-.695-4.114Z"
                    fill="#fff"
                    fillOpacity="0.85"
                  />
                  <path
                    d="M11.68 5.743C9.79 8.41 9.93 11.4 11.14 13.5l2.504 4.322a.46.46 0 0 0 .4.232h5.232a.46.46 0 0 0 .38-.718L12.44 5.743a.46.46 0 0 0-.76 0Z"
                    fill="#fff"
                  />
                </svg>
                <span>Se connecter avec Atlassian</span>
              </>
            )}
          </motion.button>

          {/* Toggle "Connexion administrateur" */}
          <div className="mt-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-white/10" />
            <button
              type="button"
              onClick={() => setShowAdminLogin((v) => !v)}
              aria-expanded={showAdminLogin}
              aria-controls="admin-login-form"
              className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 uppercase tracking-wider transition-colors"
            >
              Connexion administrateur
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform duration-200 ${
                  showAdminLogin ? 'rotate-180' : ''
                }`}
                aria-hidden="true"
              />
            </button>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Collapse : formulaire email / mot de passe (admin) */}
          <AnimatePresence initial={false}>
            {showAdminLogin && (
              <motion.form
                id="admin-login-form"
                key="admin-login-form"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                onSubmit={handleSubmit}
                className="overflow-hidden"
              >
                <div className="space-y-5 pt-6">
                  {/* Identifiant */}
                  <div>
                    <label
                      htmlFor="identifier"
                      className="block text-xs font-medium uppercase tracking-wider text-slate-300 mb-2"
                    >
                      Email ou nom d'utilisateur
                    </label>
                    <input
                      id="identifier"
                      name="identifier"
                      type="text"
                      autoComplete="username"
                      placeholder="nom@previsoft.fr"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      disabled={loading}
                      className="w-full px-4 py-3 text-sm text-white placeholder:text-slate-500 bg-white/5 rounded-xl ring-1 ring-white/10 focus:ring-2 focus:ring-blue-400/60 focus:bg-white/10 focus:outline-none transition-all disabled:opacity-50"
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-xs font-medium uppercase tracking-wider text-slate-300 mb-2"
                    >
                      Mot de passe
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      className="w-full px-4 py-3 text-sm text-white placeholder:text-slate-500 bg-white/5 rounded-xl ring-1 ring-white/10 focus:ring-2 focus:ring-blue-400/60 focus:bg-white/10 focus:outline-none transition-all disabled:opacity-50"
                    />
                  </div>

                  {/* Bouton de connexion classique (secondaire) */}
                  <motion.button
                    type="submit"
                    disabled={loading}
                    whileHover={{ scale: loading ? 1 : 1.015 }}
                    whileTap={{ scale: loading ? 1 : 0.985 }}
                    className="w-full flex justify-center items-center gap-2 px-4 py-3 text-sm font-medium text-slate-200 rounded-xl bg-white/5 ring-1 ring-white/10 hover:bg-white/10 hover:ring-blue-400/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <div className="h-4 w-4 border-2 border-slate-400/30 border-t-slate-200 rounded-full animate-spin" />
                        <span>Connexion…</span>
                      </>
                    ) : (
                      <>
                        <LogIn className="h-4 w-4" />
                        <span>Se connecter</span>
                      </>
                    )}
                  </motion.button>

                  {/* Lien inscription */}
                  <div className="text-center text-sm text-slate-400">
                    Pas encore de compte ?{' '}
                    <Link
                      to="/signup"
                      className="text-blue-400 hover:text-blue-300 font-medium transition-colors"
                    >
                      S'inscrire
                    </Link>
                  </div>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="text-center text-xs text-slate-500"
        >
          © {new Date().getFullYear()} Previsoft Servers Monitoring
        </motion.p>
      </motion.div>
    </div>
  );
}

export default Login;
